import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { DEFAULT_LAST_COMM_THRESHOLDS } from '../../../../common/constants';
import { useUserContext } from '../../../../common/utils/context';
import { formatChecklistSignOffTime } from '../../../../common/utils/checklist/checklistSignOff';
import { getLocalTime } from '../../../../common/utils/time/time';
import { formatTemperature, valueExists } from '../../../../common/utils/utils';
import { commQualities, getLastCommQuality } from '../../../../common/utils/vending-utils';
import Text from '../../../components/text/Text';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useTheme } from '../../../theme';

const StopTelemetry = ({ machine, tenantSettings }) => {
	const { tenant } = useUserContext();
	const { theme: { ui: { colors } } } = useTheme();
	const styles = useThemedStyles(createStyles);
	const [now, setNow] = useState(Date.now);
	const communication = machine?.lastCommunication;

	useEffect(() => {
		if (!communication) {
			return;
		}
		const interval = setInterval(() => setNow(Date.now()), 60000);
		return () => clearInterval(interval);
	}, [communication]);

	if (!communication) {
		return null;
	}
	const localTime = getLocalTime(tenant?.timezone, communication);
	if (!localTime.isValid()) {
		return null;
	}
	const [minThreshold, maxThreshold] = tenantSettings?.lastCommThresholds || DEFAULT_LAST_COMM_THRESHOLDS;
	const quality = getLastCommQuality({ communication, minThreshold, maxThreshold });
	const commColor = quality === commQualities.GOOD ? colors.success : (quality === commQualities.BAD ? colors.danger : colors.warning);
	const temperature = machine?.temperatureCelcius;
	const [minTemp, maxTemp] = tenantSettings?.temperatureThresholds || [];
	const outOfRange = (minTemp !== undefined && temperature / 100 < minTemp) || (maxTemp !== undefined && temperature / 100 > maxTemp);
	const tempColor = outOfRange ? colors.danger : (quality === commQualities.GOOD ? colors.success : colors.textSecondary);
	const time = formatChecklistSignOffTime(tenant?.timezone, communication);

	return (
		<View style={styles.row}>
			<View style={styles.value}>
				<Feather name="wifi" size={16} color={commColor} />
				<Text style={styles.text}>
					Last comm <Text style={{ color: commColor }}>{localTime.from(now)}, {time}</Text>
				</Text>
			</View>
			{valueExists(temperature) && (
				<View style={styles.value}>
					<Feather name="thermometer" size={16} color={tempColor} />
					{/* The server has no per-reading timestamp; use the last communication time. */}
					<Text style={styles.text}>Temp <Text style={{ color: tempColor }}>{formatTemperature(temperature)}, {time}</Text></Text>
				</View>
			)}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	row: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	value: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		maxWidth: '100%',
	},
	text: {
		...type.bodySmall,
		color: colors.textSecondary,
		flexShrink: 1,
	},
});

export default StopTelemetry;
